import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindOperationChainsQueryResponse,
  FindOperationChainsQueryParams,
} from "../types/FindOperationChains"

type FindOperationChainsClient = typeof client<
  FindOperationChainsQueryResponse,
  Error,
  never
>
type FindOperationChains = {
  data: FindOperationChainsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindOperationChainsQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindOperationChainsClient>>
  client: {
    parameters: Partial<Parameters<FindOperationChainsClient>[0]>
    return: Awaited<ReturnType<FindOperationChainsClient>>
  }
}
export const findOperationChainsQueryKey = (
  params?: FindOperationChains["queryParams"]
) =>
  [
    { url: "/v1/operation-chain/find-operation-chains" },
    ...(params ? [params] : []),
  ] as const
export type FindOperationChainsQueryKey = ReturnType<
  typeof findOperationChainsQueryKey
>
export function findOperationChainsQueryOptions<
  TData = FindOperationChains["response"],
  TQueryData = FindOperationChains["response"],
>(
  params?: FindOperationChains["queryParams"],
  options: FindOperationChains["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindOperationChains["response"],
    FindOperationChains["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findOperationChainsQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindOperationChains["data"],
        FindOperationChains["error"]
      >({
        method: "get",
        url: `/v1/operation-chain/find-operation-chains`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find operation chains
 * @link /v1/operation-chain/find-operation-chains
 */
export function useFindOperationChains<
  TData = FindOperationChains["response"],
  TQueryData = FindOperationChains["response"],
  TQueryKey extends QueryKey = FindOperationChainsQueryKey,
>(
  params?: FindOperationChains["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindOperationChains["response"],
        FindOperationChains["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindOperationChains["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindOperationChains["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findOperationChainsQueryKey(params)
  const query = useQuery<
    FindOperationChains["data"],
    FindOperationChains["error"],
    TData,
    any
  >({
    ...findOperationChainsQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindOperationChains["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
